/**
 * Responsibility: who is signed in, for the parts of the app that render.
 *
 * `firebase.ts` owns the SDK and answers "what is the token right now" for the
 * API client. This owns the React-facing half: the current user as state, so a
 * sign-in or sign-out re-renders, and a `loading` flag for the moment before
 * Firebase has restored a persisted session.
 *
 * **That loading flag is load-bearing.** On a reload Firebase reports `null`
 * first and the real user a tick later. Rendering the login screen during that
 * tick means every refresh flashes a sign-in page at somebody who is already
 * signed in, which reads as being logged out at random.
 *
 * **Switching user clears the cache.** react-query keys are not namespaced by
 * person - `['collection']` is the same key whoever is asking - so without this
 * the second person to sign in on one browser sees the first person's
 * collection until each query happens to refetch.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import { auth, firebaseConfigured, signIn, signOut } from './firebase';
import { setPageStateOwner } from '../hooks/usePageState';
import { AuthContext, type AuthState, type Identity } from './authContext';

function identityOf(user: User): Identity {
  return {
    uid: user.uid,
    email: user.email ?? '',
    displayName: user.displayName,
    photoURL: user.photoURL,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Identity | null>(null);
  const [loading, setLoading] = useState(firebaseConfigured);
  const queryClient = useQueryClient();
  const previousUid = useRef<string | null>(null);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    return onAuthStateChanged(auth, (next) => {
      const identity = next ? identityOf(next) : null;

      // Anything cached belonged to whoever was here before. Clearing on a
      // real change rather than on every callback, because Firebase fires
      // this on token refresh too and wiping the cache hourly would be a
      // needless reload of every page.
      if (previousUid.current !== (identity?.uid ?? null)) {
        setPageStateOwner(identity?.uid ?? null);
        if (previousUid.current !== null) {
          queryClient.clear();
        }
        previousUid.current = identity?.uid ?? null;
      }

      setUser(identity);
      setLoading(false);
    });
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({ user, loading, configured: firebaseConfigured, signIn, signOut }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
